import './polyfills'; // Must be first — patches Array/String.prototype.at for Chrome ≤ 80
import { ChakraProvider } from '@chakra-ui/react';
import { Purchases } from '@revenuecat/purchases-capacitor';
import { Capacitor } from '@capacitor/core';
import { Global, ClassNames, css } from '@emotion/react';
import { ToastContainer, cssTransition } from 'react-toastify';
import 'animate.css/animate.css';
import 'react-toastify/dist/ReactToastify.css';
import { posthog } from 'posthog-js';
import { QueryClient, QueryClientProvider } from 'react-query';
import { FC, useEffect, useState, useRef } from 'react';
import { render } from 'react-dom';
import { Redirect, BrowserRouter as Router, Route, useLocation, Switch, useHistory } from 'react-router-dom';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';
import { App } from '@capacitor/app';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { AxiosError } from 'axios';
import { setPurchasesReady } from './utils/purchasesInit';
import { initializeSentry, Sentry } from './utils/sentryConfig';
import './i18n';
import { AppErrorBoundary } from './components/ErrorBoundary';
import initializePosthog from './utils/initializePosthog';
import initializeOfflineToast, { checkIsOnline } from './utils/initializeOfflineToast';
import * as backHandling from './utils/backHandling';
import SplashScreen from './components/SplashScreen';
// Import debug utilities only in development
if (import.meta.env.DEV) {
  import('./utils/logoCacheDebug');
  // Import AppsFlyer testing utilities for development
  import('./utils/appsflyerTesting');
  import('./utils/appsflyerIntegrationVerifier');
  // Import Sentry testing utilities
  import('./utils/sentryTesting');
}
import { buildPageSpacing, spacing, getGlobalStyles, fontStyles, colors, theme } from './styles';
import { setGlobalHistory } from './utils/routing';

import { runAllMigrations } from './migrations';
import * as stores from './stores';
import CollectionsPage from './components/CollectionsPage';
import SmartSummaryDetailPage from './components/SmartSummaryDetailPage';
import MarketsPage from './components/MarketsPage';
import IdeasPage from './components/IdeasPage';
import AllMarketNewsPage from './components/AllMarketNewsPage';
import TradesHistoryPage from './components/TradesHistoryPage';
import AIArenaPage from './components/AIArenaPage';
import CollectionPage from './components/CollectionPage';
import ConfirmCollectionCopy from './components/CollectionPage/ConfirmCollectionCopy';
import SymbolPage from './components/SymbolPage';
import MorePage from './components/MorePage';
import PlaygroundPage from './components/PlaygroundPage';
import VideoShowcasePage from './components/VideoShowcasePage';
import { initializeTheme, setupThemeListener } from './utils/themeManager';
import { initializeFontSize } from './utils/fontSizeManager';
import WatchlistSearchPage from './components/WatchlistSearchPage';
import ChatPage from './components/ChatPage';
import NotificationCustomizePage from './components/NotificationCustomizePage';
import NotificationsPage from './components/NotificationsPage/NotificationHistoryPage';
import BrokerageSettingsPage from './components/Plaid/BrokerageSettingsPage';
import {
  PortfoliosPage,
  AddPortfolio,
  ViewPortfolio,
  AddTickers,
  EditPortfolio,
  UpdatePortfolioPage,
} from './components/Portfolios';
// Onboarding
import { Welcome } from './components/OnboardingView/Page/Welcome';
import { OnboardingView } from './components/OnboardingView/Page/OnboardingView';
import { PurchaseResult } from './components/OnboardingView/Page/Result';
import { OneTimeOfferPage } from './components/OneTimeOfferPage';
import * as storage from './storage';
import { PushNotificationService } from './services/pushNotificationService';
import { useGlobalPaywallTrigger } from './hooks/useGlobalPaywallTrigger';
import { useKeyboardState } from './hooks/useKeyboardState';
import { PaymentModalByPreference } from './components/PaymentModalByPreference/PaymentModalByPreference';
import { appsflyerService } from './services/appsflyerService';
import { checkTrialConversion } from './services/subscriptionStatusTracker';
import TabOnboardingOverlay from './components/TabOnboardingOverlay';
import AppUpdateDialog from './components/AppUpdateDialog';
import { useAppUpdateCheck } from './hooks/useAppUpdateCheck';
import { initializeRemoteConfig } from './hooks/useRemoteConfig';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        const axiosError = error as AxiosError;
        // Don't retry on network errors - let the offline toast handle it
        if (axiosError.isAxiosError && !axiosError.response) {
          return false;
        }
        // Don't retry on 4xx errors (client errors)
        if (
          axiosError.isAxiosError &&
          axiosError.response &&
          axiosError.response.status >= 400 &&
          axiosError.response.status < 500
        ) {
          return false;
        }
        // Retry up to 2 times for other errors (500s, timeouts, etc.)
        return failureCount < 2;
      },
    },
  },
});

// Only configure Purchases if we are on a native platform.
// Store the promise so downstream callers can await initialization.
if (Capacitor.isNativePlatform()) {
  const ready = Purchases.configure({
    apiKey: Capacitor.getPlatform() === 'ios' ? 'appl_WzwoHcGpOkytDhvIXdkxELufsEY' : 'goog_KvaRdNEIDCrurgzrpZLEgfiKAJp',
  }).catch((e) => {
    console.error('[RevenueCat] configure() failed:', e);
  });
  setPurchasesReady(ready);
}

export const updateNativeUI = async (isDark: boolean) => {
  if (Capacitor.isNativePlatform()) {
    try {
      await StatusBar.setStyle({
        style: isDark ? Style.Dark : Style.Light,
      });

      if (Capacitor.getPlatform() === 'android') {
        await StatusBar.setBackgroundColor({
          color: isDark ? colors.modes.dark.statusBar : colors.modes.light.statusBar,
        });
      }
    } catch (error) {
      console.warn('Error updating native UI:', error);
    }
  }
};

// Initialize Sentry BEFORE any React rendering
initializeSentry();

backHandling.init();
backHandling.addBackListener(({ canGoBack }) => {
  if (canGoBack) {
    window.history.back();
  } else {
    App.exitApp();
  }
});

const RouterApp: FC = () => {
  const location = useLocation();
  const history = useHistory();

  // Check if onboarding has been completed (null means first time opening the app)
  const hasCompletedOnboarding = localStorage.getItem('hasCompletedOnboarding') === 'true';

  // Determine redirect path synchronously to avoid race condition
  const getInitialRedirectPath = () => {
    if (!hasCompletedOnboarding) {
      return '/welcome';
    } else {
      // Redirect completed onboarding users to portfolios on app reload
      return '/portfolios';
    }
  };

  const [redirectPath] = useState<string>(getInitialRedirectPath());

  useEffect(() => {
    posthog.capture('$pageview');

    // Add navigation breadcrumb for Sentry
    Sentry.addBreadcrumb({
      category: 'navigation',
      message: `Navigated to ${location.pathname}`,
      data: {
        pathname: location.pathname,
        search: location.search,
      },
      level: 'info',
    });
  }, [location]);

  useEffect(() => {
    initializeTheme();

    const cleanup = setupThemeListener();
    return cleanup;
  }, []);

  // Initialize global history for programmatic navigation
  useEffect(() => {
    setGlobalHistory(history);
  }, [history]);

  // Handle ?showPayment=true query parameter to trigger payment modal
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('showPayment') === 'true') {
      stores.showPaymentModalByPreference('query_param');

      // Remove the query param from URL after triggering
      params.delete('showPayment');
      const newSearch = params.toString();
      history.replace({
        pathname: location.pathname,
        search: newSearch ? `?${newSearch}` : '',
      });
    }
  }, [location, history]);

  // Expose current path for tab onboarding check
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('routeChange', { detail: location.pathname }));
  }, [location.pathname]);

  return (
    <Switch>
      <Route exact path="/" render={() => <Redirect to={redirectPath} />} />
      <Route exact path="/welcome" component={Welcome} />
      <Route exact path="/onboarding" render={() => <Redirect to="/onboarding/0" />} />
      <Route exact path="/onboarding/:step" component={OnboardingView} />
      <Route exact path="/onboarding/create-portfolio" render={() => <AddPortfolio isOnboarding={true} />} />
      <Route
        exact
        path="/onboarding/view-portfolio/:id"
        render={(props) => <ViewPortfolio {...props} isOnboarding={true} />}
      />
      <Route exact path="/payment" render={() => <Redirect to="/ideas/collections?showPayment=true" />} />
      <Route exact path="/result" component={PurchaseResult} />
      <Route exact path="/one-time-offer" component={OneTimeOfferPage} />
      <Route exact path="/ideas" component={IdeasPage} />
      <Route exact path="/ideas/collections" component={CollectionsPage} />
      <Route exact path="/ideas/picks" component={TradesHistoryPage} />
      <Route exact path="/ideas/ai-arena" component={AIArenaPage} />
      <Route exact path="/ideas/news" component={AllMarketNewsPage} />
      <Route exact path="/search" render={() => <Redirect to="/ideas/collections" />} />
      <Route exact path="/ideas/screeners" render={() => <Redirect to="/ideas/collections" />} />
      <Route exact path="/portfolio-search" component={CollectionsPage} />
      <Route path="/more" component={MorePage} />
      <Route path="/symbol/:symbol" component={SymbolPage} />
      <Route exact path="/collection/:collectionId/confirm" component={ConfirmCollectionCopy} />
      <Route path="/collection/:collectionId" component={CollectionPage} />
      <Route path="/playground" component={PlaygroundPage} />
      <Route exact path="/watchlist-search" component={WatchlistSearchPage} />
      <Route path="/markets" component={MarketsPage} />
      <Route path="/smart-summary/:id" component={SmartSummaryDetailPage} />
      <Route path="/notifications" component={NotificationsPage} />
      <Route path="/chat" component={ChatPage} />
      <Route path="/notification-customize" component={NotificationCustomizePage} />
      <Route path="/settings/brokerage" component={BrokerageSettingsPage} />
      <Route exact path="/portfolios" render={() => <ViewPortfolio isDefaultView />} />
      <Route exact path="/portfolios/compare" component={PortfoliosPage} />
      <Route exact path="/portfolios/add" component={AddPortfolio} />
      <Route exact path="/portfolios/view/:id" component={ViewPortfolio} />
      <Route exact path="/portfolios/:id/add-tickers" component={AddTickers} />
      <Route exact path="/portfolios/add-tickers/new" component={AddTickers} />
      <Route exact path="/portfolios/edit/:id" component={EditPortfolio} />
      <Route exact path="/portfolios/update/:id" component={UpdatePortfolioPage} />
    </Switch>
  );
};
// Delay to ensure navigation bar is rendered before showing tab onboarding
const TAB_ONBOARDING_DISPLAY_DELAY = 500; // ms

// Pages where navigation bar is not visible (onboarding flow)
const PAGES_WITHOUT_NAV_BAR = ['/welcome', '/result', '/one-time-offer'];

const AppWrapper: FC = () => {
  const {
    isAppReady,
    isSearchOnboardingPopoverShown,
    isBookmarkOnboardingPopoverShown,
    isPaymentModalByPreferenceShown,
  } = stores.General.useState();
  const [showTabOnboarding, setShowTabOnboarding] = useState(false);
  const [currentPath, setCurrentPath] = useState<string>(window.location.pathname);
  const [isOffline, setIsOffline] = useState(!checkIsOnline());
  const [allowOfflineEntry, setAllowOfflineEntry] = useState(false);
  const tabOnboardingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const offlineTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // App update check - shows dialog when new version is available (iOS and Android)
  const { isDialogVisible: showUpdateDialog, dismissUpdate, platform: updatePlatform } = useAppUpdateCheck();

  // Global paywall trigger - shows paywall every 2 app opens for non-subscribers
  useGlobalPaywallTrigger();

  // Allow offline entry after 4 seconds regardless of whether init finished.
  // Previously this gated on isAppReady, but if a network call inside the init
  // sequence hangs (e.g. RevenueCat getCustomerInfo with no internet), isAppReady
  // never becomes true and the escape never fires — leaving the app stuck on the
  // splash screen forever. Decoupling from isAppReady fixes that catch-22.
  useEffect(() => {
    if (isOffline && !allowOfflineEntry) {
      console.log('[OFFLINE] Offline detected. Starting 4s timeout to allow entry...');
      offlineTimeoutRef.current = setTimeout(() => {
        console.log('[OFFLINE] 4s timeout reached. Allowing offline entry to app.');
        setAllowOfflineEntry(true);
      }, 4000);
    }

    // Clear timeout if we come back online or if we already allowed entry
    if (!isOffline || allowOfflineEntry) {
      if (offlineTimeoutRef.current) {
        clearTimeout(offlineTimeoutRef.current);
        offlineTimeoutRef.current = null;
      }
    }

    return () => {
      if (offlineTimeoutRef.current) {
        clearTimeout(offlineTimeoutRef.current);
      }
    };
  }, [isOffline, allowOfflineEntry]);

  // Monitor network connectivity for splash screen
  useEffect(() => {
    const RELOAD_ATTEMPT_KEY = 'bloom_reload_attempts';
    const MAX_RELOAD_ATTEMPTS = 3;
    const RELOAD_TIMEOUT_MS = 500;

    const handleOffline = () => setIsOffline(true);
    const handleOnline = () => {
      // Wait a bit to ensure we're truly online
      setTimeout(() => {
        const nowOnline = checkIsOnline();
        setIsOffline(!nowOnline);

        // Reset allowOfflineEntry when we come back online
        if (nowOnline) {
          setAllowOfflineEntry(false);
        }

        // If we came back online and app is not ready, trigger re-initialization
        if (nowOnline && !isAppReady) {
          // Check reload attempts to prevent infinite loops
          const attempts = parseInt(sessionStorage.getItem(RELOAD_ATTEMPT_KEY) || '0', 10);

          if (attempts < MAX_RELOAD_ATTEMPTS) {
            if (import.meta.env.DEV) {
              console.log(
                `[STARTUP] 🔄 Connection restored, retrying app initialization (attempt ${attempts + 1}/${MAX_RELOAD_ATTEMPTS})...`,
              );
            }
            sessionStorage.setItem(RELOAD_ATTEMPT_KEY, String(attempts + 1));
            window.location.reload();
          } else {
            if (import.meta.env.DEV) {
              console.warn(
                '[STARTUP] ⚠️ Max reload attempts reached. App may have initialization issues beyond connectivity.',
              );
            }
            // Clear attempts after waiting, allowing future retries
            setTimeout(() => sessionStorage.removeItem(RELOAD_ATTEMPT_KEY), 60000);
          }
        }
      }, RELOAD_TIMEOUT_MS);
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, [isAppReady]);

  // Track current route path
  useEffect(() => {
    const handleRouteChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      setCurrentPath(customEvent.detail);
    };

    window.addEventListener('routeChange', handleRouteChange);
    return () => {
      window.removeEventListener('routeChange', handleRouteChange);
    };
  }, []);

  // Check if we should show tab onboarding (after user completes onboarding flow)
  useEffect(() => {
    const checkTabOnboarding = () => {
      // Only check if app is ready to avoid race conditions
      if (!isAppReady) return;

      try {
        const hasSeenTabOnboarding = localStorage.getItem('hasSeenTabOnboarding');
        const hasCompletedOnboarding = localStorage.getItem('hasCompletedOnboarding');

        // Don't show tab onboarding on onboarding-related pages (no navigation bar present)
        const isOnboardingPage = currentPath.startsWith('/onboarding') || PAGES_WITHOUT_NAV_BAR.includes(currentPath);

        // Hide overlay if we navigate to an onboarding page
        if (isOnboardingPage) {
          if (tabOnboardingTimerRef.current) {
            clearTimeout(tabOnboardingTimerRef.current);
            tabOnboardingTimerRef.current = null;
          }
          setShowTabOnboarding(false);
          return;
        }

        // Show overlay if user has completed onboarding but hasn't seen tab onboarding yet
        if (hasCompletedOnboarding === 'true' && !hasSeenTabOnboarding) {
          // Clear any existing timer before setting a new one
          if (tabOnboardingTimerRef.current) {
            clearTimeout(tabOnboardingTimerRef.current);
          }
          // Delay to ensure navigation bar is rendered
          tabOnboardingTimerRef.current = setTimeout(() => {
            setShowTabOnboarding(true);
          }, TAB_ONBOARDING_DISPLAY_DELAY);
        }
      } catch (e) {
        console.warn('Error accessing localStorage for tab onboarding check:', e);
      }
    };

    // Check on mount and when onboarding completes or route changes
    checkTabOnboarding();

    window.addEventListener('onboardingCompleted', checkTabOnboarding);
    return () => {
      window.removeEventListener('onboardingCompleted', checkTabOnboarding);
      // Clean up timer if component unmounts
      if (tabOnboardingTimerRef.current) {
        clearTimeout(tabOnboardingTimerRef.current);
      }
    };
    // Note: showTabOnboarding intentionally excluded to prevent re-triggering after overlay shows
  }, [isAppReady, currentPath]);

  const handleDismissTabOnboarding = () => {
    setShowTabOnboarding(false);
    try {
      localStorage.setItem('hasSeenTabOnboarding', 'true');
    } catch (e) {
      console.warn('Error setting hasSeenTabOnboarding in localStorage:', e);
    }
  };

  useEffect(() => {
    // Store cleanup functions
    let cleanupOfflineToast: (() => void) | undefined;
    let cleanupDarkMode: (() => void) | undefined;

    (async () => {
      console.log('[STARTUP] 🚀 Starting app initialization...');

      try {
        await initializePosthog();
      } catch (e) {
        console.warn('Error initializing Posthog:', e);
      }

      // Initialize AppsFlyer SDK in the background (non-blocking)
      appsflyerService
        .initialize()
        .then(() => console.log('[STARTUP] ✅ AppsFlyer SDK initialized successfully'))
        .catch((e) => {
          console.error('[STARTUP] ❌ AppsFlyer initialization failed:', e);
          console.error('[STARTUP] AppsFlyer error details:', JSON.stringify(e, null, 2));
        });

      // Initialize Firebase Remote Config in the background (non-blocking)
      initializeRemoteConfig().catch((e) => console.error('[STARTUP] ❌ Remote config background init failed:', e));

      // Store cleanup function for offline toast
      cleanupOfflineToast = initializeOfflineToast();

      try {
        await runAllMigrations();
      } catch (e) {
        console.error('[STARTUP] Migration failed, continuing:', e);
      }
      await Promise.all([
        stores.initializeGeneral(),
        stores.initializeTableOptions(),
        stores.initializePurchase(),
        storage.initializePortfolios(),
      ]);

      // Check for trial conversion after stores are initialized
      try {
        await checkTrialConversion();
        console.log('[STARTUP] ✅ Trial status check completed');
      } catch (e) {
        console.error('[STARTUP] Error checking trial conversion:', e);
      }

      const darkModeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      updateNativeUI(darkModeMediaQuery.matches);

      const handleDarkModeChange = (e: MediaQueryListEvent) => {
        updateNativeUI(e.matches);
      };

      darkModeMediaQuery.addEventListener('change', handleDarkModeChange);

      // Store dark mode cleanup function
      cleanupDarkMode = () => {
        darkModeMediaQuery.removeEventListener('change', handleDarkModeChange);
      };

      // Configure keyboard behavior per platform
      if (Capacitor.getPlatform() === 'ios') {
        try {
          await StatusBar.setOverlaysWebView({ overlay: true });
          await Keyboard.setAccessoryBarVisible({ isVisible: true });
          await Keyboard.setResizeMode({ mode: KeyboardResize.None });
        } catch (error) {
          console.warn('Error configuring iOS specific features:', error);
        }
      } else if (Capacitor.getPlatform() === 'android') {
        try {
          await StatusBar.setOverlaysWebView({ overlay: true });
          await Keyboard.setResizeMode({ mode: KeyboardResize.None });
        } catch (error) {
          console.warn('Error configuring Android keyboard features:', error);
        }
      }

      stores.General.update((s) => {
        s.isAppReady = true;
        s.hasSeenSubscriptionTakeoverThisSession = false;
      });

      // Initialize push notification service if user has enabled notifications
      // This ensures listeners are ready when app opens from notification tap
      try {
        const savedPreferences = localStorage.getItem('notificationPreferences');
        if (savedPreferences) {
          const preferences = JSON.parse(savedPreferences);
          const enabledTypes = preferences
            .filter((pref: { enabled: boolean }) => pref.enabled)
            .map((pref: { id: string }) => pref.id);

          if (enabledTypes.length > 0) {
            console.log('[STARTUP] Found enabled notifications, initializing push service...');

            // Get current portfolio symbols for notification config
            const portfolios = await storage.getPortfolios();
            const allSymbols = Array.from(new Set(portfolios.flatMap((p) => p.symbols || [])));

            // Initialize the service in the background (don't await to avoid blocking startup)
            PushNotificationService.initialize({
              notificationTypes: enabledTypes,
              watchlistSymbols: allSymbols,
            })
              .then((success) => {
                if (success) {
                  console.log('[STARTUP] ✅ Push notification service initialized successfully');
                } else {
                  console.log('[STARTUP] ❌ Push notification service initialization failed');
                }
              })
              .catch((error) => {
                console.error('[STARTUP] Error initializing push notification service:', error);
              });
          } else {
            console.log('[STARTUP] No enabled notifications found, skipping push service initialization');
          }

          // Always attempt daily device sync if user has any notification preferences (enabled or disabled)
          // This ensures backend stays in sync even when notifications are temporarily disabled
          console.log('[STARTUP] Attempting daily device sync for existing notification preferences...');
          PushNotificationService.performDailyDeviceSync()
            .then((success) => {
              if (success) {
                console.log('[STARTUP] ✅ Daily device sync completed');
              } else {
                console.log('[STARTUP] ❌ Daily device sync failed or skipped');
              }
            })
            .catch((error) => {
              console.error('[STARTUP] Error performing daily device sync:', error);
            });
        } else {
          console.log('[STARTUP] No notification preferences found, skipping push service initialization');
        }
      } catch (error) {
        console.error('[STARTUP] Error checking notification preferences during startup:', error);
      }

      // Only notify if we are on the native platform
      if (Capacitor.isNativePlatform()) {
        try {
          // Set a timeout to prevent blocking indefinitely
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('CapacitorUpdater timeout')), 5000),
          );

          await Promise.race([CapacitorUpdater.notifyAppReady(), timeoutPromise]);
        } catch (e) {
          console.warn('Error in CapacitorUpdater:', e);
        }

        // Log OTA update failures to Sentry for visibility
        // Using captureMessage at warning level (not captureException) because OTA
        // download/update failures are transient network issues — the app continues
        // running on the current bundle, so these are non-fatal. Fingerprinted to
        // prevent noise storms (BLOOM-FRONTEND-WEB-10K, BLOOM-FRONTEND-WEB-10M).
        // void: AppWrapper never unmounts, so cleanup isn't needed
        void CapacitorUpdater.addListener('updateFailed', (info) => {
          Sentry.withScope((scope) => {
            scope.setFingerprint(['capgo-ota-update-failed']);
            scope.setLevel('warning');
            scope.setExtra('bundle', info);
            Sentry.captureMessage('Capgo OTA update failed', 'warning');
          });
        });
        void CapacitorUpdater.addListener('downloadFailed', (info) => {
          Sentry.withScope((scope) => {
            scope.setFingerprint(['capgo-ota-download-failed']);
            scope.setLevel('warning');
            scope.setExtra('bundle', info);
            Sentry.captureMessage('Capgo OTA download failed', 'warning');
          });
        });
      }
    })();

    // Return cleanup function that cleans up all event listeners
    return () => {
      if (cleanupOfflineToast) {
        cleanupOfflineToast();
      }
      if (cleanupDarkMode) {
        cleanupDarkMode();
      }
    };
  }, []);

  // Manage keyboard visibility state on native platforms
  useKeyboardState();

  // Show splash screen while loading or if offline during initial load
  // Allow entry after 4 seconds if offline, regardless of isAppReady
  if (!isAppReady && !(isOffline && allowOfflineEntry)) {
    return <SplashScreen isOffline={isOffline} />;
  }

  return (
    <AppErrorBoundary>
      <div
        onClick={
          isSearchOnboardingPopoverShown || isBookmarkOnboardingPopoverShown
            ? () => {
                stores.General.update((s) => {
                  s.isSearchOnboardingPopoverShown = false;
                  s.isBookmarkOnboardingPopoverShown = false;
                });
              }
            : undefined
        }
      >
        <div
          css={
            (isSearchOnboardingPopoverShown || isBookmarkOnboardingPopoverShown) &&
            css`
              pointer-events: none;
            `
          }
        >
          <Router>
            <RouterApp />
            <PaymentModalByPreference />
            {showTabOnboarding && <TabOnboardingOverlay onDismiss={handleDismissTabOnboarding} />}
            <AppUpdateDialog
              isOpen={showUpdateDialog && !isPaymentModalByPreferenceShown}
              onClose={dismissUpdate}
              platform={updatePlatform}
            />
          </Router>
        </div>
      </div>
    </AppErrorBoundary>
  );
};

initializeTheme();
initializeFontSize();

// Expose PushNotificationService for debugging
if (typeof window !== 'undefined') {
  (window as any).PushNotificationService = PushNotificationService;
}

// Render VideoShowcasePage directly, bypassing AppWrapper initialization
const isVideoShowcase = window.location.pathname === '/video-showcase';

render(
  <div>
    <ChakraProvider theme={theme}>
      <QueryClientProvider client={queryClient}>
        {isVideoShowcase ? <VideoShowcasePage /> : <AppWrapper />}
        <Global styles={getGlobalStyles()} />
        <ClassNames>
          {({ css }) => (
            <ToastContainer
              position="bottom-center"
              hideProgressBar
              draggable={false}
              newestOnTop
              closeButton={false}
              autoClose={3000}
              limit={3}
              className={css`
                position: fixed;
                z-index: 1100;
                padding: 0 ${buildPageSpacing(spacing.regular, 'RIGHT')} 0 ${buildPageSpacing(spacing.regular, 'LEFT')};
                margin-bottom: calc(8px + ${buildPageSpacing(spacing.giga, 'BOTTOM')});
              `}
              toastClassName={css`
                min-height: initial;
                color: ${colors.liquorice};
                background-color: ${colors.sugar};
                padding: ${spacing.deci};
                ${fontStyles.cat};
              `}
              bodyClassName={css`
                width: 100%;
                padding: 0;
              `}
              transition={cssTransition({
                enter: 'animate__animated animate__fadeInUp animate__faster',
                exit: 'animate__animated animate__fadeOutDown animate__faster',
              })}
            />
          )}
        </ClassNames>
      </QueryClientProvider>
    </ChakraProvider>
  </div>,
  document.getElementById('react-entry'),
);
